import { useMemo } from "react";
import useDeleteConversation from "../../api/api-hooks/conversations/useDeleteConversation";

import useGPTConversations from "../../api/api-hooks/conversations/useGPTConversations";
import SkeletonLoader from "../../elements/SkeletonLoader";
import { SetBriePage, SetConvoID } from "./BrieChatMenu";

import BrieIcon from "./BrieIcon";
import ConversationCard from "./ConversationCard";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { SearchGPTMessage } from "./GPTConvoTypes";

dayjs.extend(relativeTime);

export interface BrieConversation {
  id: string;
  Type: "brie-chatgpt";
  Messages?: SearchGPTMessage[];
  Title: string;
  LastUpdated: any;
  Participants?: [];
}

interface BrieMessagesProp {
  setPage: SetBriePage;
  setConversationID: SetConvoID;
}

const IconBrie = (
  <div
    className="brie-icon"
    style={{ width: 37, height: 37, boxShadow: "unset" }}
  >
    <BrieIcon transform="scale(.65, .65)" />
  </div>
);

const IconFeedback = (
  <i
    className="mdi mdi-comment-quote"
    style={{ fontSize: 32, lineHeight: 1.13 }}
  />
);

const BrieMessages = ({ setPage, setConversationID }: BrieMessagesProp) => {
  const { conversations: gptConversations, conversationsLoading } =
    useGPTConversations();
  const { deleteConversation } = useDeleteConversation();

  const sortedGptConvos = useMemo(() => {
    return gptConversations.sort((a, b) =>
      a.LastUpdated > b.LastUpdated ? -1 : a.LastUpdated < b.LastUpdated ? 1 : 0
    );
  }, [gptConversations]);

  return (
    <div className="mx-3 mt-2 font-weight-500 text-dark">
      <h4 className="text-lighter text-uppercase">Brie Intelligent Search</h4>
      {conversationsLoading && (
        <>
          <SkeletonLoader />
          <SkeletonLoader />
          <SkeletonLoader />
        </>
      )}
      {!conversationsLoading && gptConversations.length < 1 && (
        <div
          className="py-2 px-3 border rounded text-nowrap cursor-pointer"
          style={{ fontSize: 13 }}
        >
          No Conversations Found
        </div>
      )}
      {!conversationsLoading &&
        sortedGptConvos.map((c) => {
          const lastUpdated = dayjs(c.LastUpdated).fromNow();
          const title = c.Title;
          const icon = getIcon(c.Type);

          return (
            <ConversationCard
              key={c.id}
              title={title}
              description={lastUpdated}
              icon={icon}
              onClick={() => openConversation(c.id)}
              onDelete={deleteConversationByID(c.id)}
            />
          );
        })}
      <h4 className="text-lighter text-uppercase mt-4">Feedback</h4>
      <ConversationCard
        title="Feedback Messages"
        description="All the feedback you've given"
        icon={IconFeedback}
        onClick={() => setPage("chat-feedback")}
      />

      <div
        onClick={startNewConversation}
        className="bg-primary text-white text-center py-2 px-3 border border-medium rounded text-nowrap cursor-pointer"
        style={{
          fontSize: 15,
          width: 250,
          position: "absolute",
          bottom: 10,
          left: "calc(50% - 125px)",
          cursor: "pointer",
        }}
      >
        Start a new conversation
        <i className="mdi mdi-send ml-2" />
      </div>
    </div>
  );

  function getIcon(type: string) {
    if (type === "brie-chatgpt") {
      return IconBrie;
    }
  }

  function startNewConversation() {
    setPage("chat-gpt");
    setConversationID("new");
  }

  function openConversation(convoID: string) {
    setPage("chat-gpt");
    setConversationID(convoID);
  }

  function deleteConversationByID(convoID: string) {
    return () => {
      deleteConversation({ conversationID: convoID });
    };
  }
};

export default BrieMessages;
